import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ERROR_STATUS_LABELS, type AppErrorDetailDTO, type AppErrorRowDTO, type ErrorStatus } from '@bifeai/shared';
import { Badge, Button, Callout, Card, CardBody, Dialog, EmptyState, Field, PageHeader, Skeleton, Textarea, useToast, type Tone } from '@bifeai/ui';
import { CircleCheck, EyeOff, RotateCcw, Search } from 'lucide-react';
import { useState } from 'react';
import { api, errorMessage } from '../lib/api';
import { formatDateTime, timeAgo } from '../lib/format';

const FILTERS: { key: string; label: string }[] = [
  { key: 'open', label: 'Abiertos' },
  { key: 'RESOLVED', label: 'Resueltos' },
  { key: 'IGNORED', label: 'Ignorados' },
  { key: 'all', label: 'Todos' },
];

const TONES: Record<ErrorStatus, Tone> = { NEW: 'danger', INVESTIGATING: 'warning', RESOLVED: 'success', IGNORED: 'neutral' };
const SOURCE: Record<string, string> = { api: 'API', web: 'App clientes', admin: 'Panel admin' };

/**
 * Errores de la aplicación. No se mandan mails: se ven acá.
 * Un error resuelto que vuelve a pasar se reabre solo y queda marcado.
 */
export function ErrorsPage() {
  const [filter, setFilter] = useState('open');
  const [openId, setOpenId] = useState<string | null>(null);
  const list = useQuery({
    queryKey: ['errors', 'list', filter],
    queryFn: () => api.get<AppErrorRowDTO[]>(`/platform/errors?status=${filter}`),
    refetchInterval: 30_000,
  });

  return (
    <>
      <PageHeader title="Errores" subtitle="Lo que falla en la API, la app de clientes o este panel. Marcá como resuelto lo que ya arreglaste." />
      <div className="bf-segmented" role="tablist" aria-label="Filtrar errores" style={{ width: 'fit-content' }}>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            role="tab"
            aria-selected={filter === f.key}
            aria-pressed={filter === f.key}
            onClick={() => setFilter(f.key)}
            style={{ width: 'auto', padding: '0 14px', fontWeight: 600, fontSize: 'var(--bf-text-sm)' }}
          >
            {f.label}
          </button>
        ))}
      </div>
      <Card>
        <CardBody flush>
          {list.isPending && (
            <div style={{ padding: '8px 22px 16px', display: 'grid', gap: 12 }}>
              <Skeleton />
              <Skeleton />
            </div>
          )}
          {list.data?.length === 0 && (
            <EmptyState
              icon={<CircleCheck />}
              title={filter === 'open' ? 'Sin errores abiertos' : 'No hay errores en esta lista'}
              text={filter === 'open' ? 'Cuando algo falle, aparece acá al instante.' : undefined}
            />
          )}
          {list.data && list.data.length > 0 && (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Error</th>
                    <th>Origen</th>
                    <th>Veces</th>
                    <th>Clientes</th>
                    <th>Último</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.map((e) => (
                    <tr key={e.id} data-clickable="true" onClick={() => setOpenId(e.id)}>
                      <td style={{ maxWidth: 420 }}>
                        <div className="bf-cell-main" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {e.message}
                        </div>
                        <div className="bf-cell-sub">
                          {e.name ?? 'Error'}
                          {e.route ? ` en ${e.route}` : ''}
                        </div>
                      </td>
                      <td>{SOURCE[e.source] ?? e.source}</td>
                      <td className="bf-num">{e.count}</td>
                      <td className="bf-num">{e.tenantsAffected}</td>
                      <td className="bf-cell-sub">{timeAgo(e.lastSeen)}</td>
                      <td>
                        <Badge tone={TONES[e.status]} dot>
                          {ERROR_STATUS_LABELS[e.status]}
                        </Badge>
                        {e.reopenCount > 0 && e.status === 'NEW' && (
                          <div className="bf-cell-sub" style={{ marginTop: 4 }}>
                            Volvió a aparecer
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
      {openId && <ErrorDetail id={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}

function ErrorDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [note, setNote] = useState('');
  const detail = useQuery({ queryKey: ['errors', 'detail', id], queryFn: () => api.get<AppErrorDetailDTO>(`/platform/errors/${id}`) });
  const update = useMutation({
    mutationFn: (status: ErrorStatus) => api.patch<AppErrorDetailDTO>(`/platform/errors/${id}`, { status, note: note || undefined }),
    onSuccess: async (res) => {
      toast.success(`Error marcado como ${ERROR_STATUS_LABELS[res.status].toLowerCase()}`);
      await qc.invalidateQueries({ queryKey: ['errors'] });
      if (res.status === 'RESOLVED' || res.status === 'IGNORED') onClose();
    },
    onError: (err) => toast.error('No se pudo actualizar', errorMessage(err)),
  });
  const e = detail.data;

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      wide
      title="Detalle del error"
      description={e ? `${SOURCE[e.source] ?? e.source}, ${e.count} veces` : undefined}
    >
      {!e ? (
        <Skeleton style={{ height: 120 }} />
      ) : (
        <div className="bf-form">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <Badge tone={TONES[e.status]} dot>
              {ERROR_STATUS_LABELS[e.status]}
            </Badge>
            {e.reopenCount > 0 && (
              <Badge tone="warning">
                Se reabrió {e.reopenCount} {e.reopenCount === 1 ? 'vez' : 'veces'}
              </Badge>
            )}
            {e.release && <Badge>Versión {e.release}</Badge>}
          </div>
          <div>
            <p className="bf-cell-main" style={{ fontSize: 'var(--bf-text-lg)' }}>
              {e.message}
            </p>
            <p className="bf-cell-sub">
              {e.name ?? 'Error'}
              {e.route ? ` en ${e.route}` : ''}. Primera vez {formatDateTime(e.firstSeen)}, última {formatDateTime(e.lastSeen)}
            </p>
          </div>
          {e.resolvedAt && e.status === 'RESOLVED' && (
            <Callout>
              Resuelto el {formatDateTime(e.resolvedAt)}
              {e.resolutionNote ? `: ${e.resolutionNote}` : '.'}
            </Callout>
          )}
          {e.stack && (
            <details>
              <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Detalle técnico</summary>
              <pre
                style={{
                  marginTop: 10,
                  padding: 14,
                  maxHeight: 260,
                  overflow: 'auto',
                  borderRadius: 'var(--bf-radius-md)',
                  background: 'var(--bf-surface-sunken)',
                  border: '1px solid var(--bf-line)',
                  fontSize: 12,
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {e.stack}
              </pre>
            </details>
          )}
          <div>
            <p className="bf-label" style={{ marginBottom: 8 }}>
              Últimas ocurrencias
            </p>
            <div style={{ border: '1px solid var(--bf-line)', borderRadius: 'var(--bf-radius-md)', maxHeight: 200, overflow: 'auto' }}>
              {e.events.map((ev) => (
                <div
                  key={ev.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 12,
                    padding: '10px 14px',
                    borderBottom: '1px solid var(--bf-line)',
                  }}
                >
                  <span>{ev.tenantName ?? 'Sin cliente (antes del login o panel)'}</span>
                  <span className="bf-cell-sub">{formatDateTime(ev.createdAt)}</span>
                </div>
              ))}
            </div>
          </div>
          {e.status !== 'RESOLVED' && (
            <Field label="Nota (opcional)" hint="Qué se arregló o por qué se ignora. Queda guardada en el error.">
              <Textarea
                value={note}
                onChange={(ev) => setNote(ev.target.value)}
                placeholder="Ej: corregido el cálculo de vencimientos en la versión 1.2"
              />
            </Field>
          )}
          <div className="bf-form-actions" style={{ flexWrap: 'wrap' }}>
            {e.status === 'RESOLVED' || e.status === 'IGNORED' ? (
              <Button icon={<RotateCcw />} onClick={() => update.mutate('NEW')} loading={update.isPending}>
                Reabrir
              </Button>
            ) : (
              <>
                <Button
                  icon={<EyeOff />}
                  variant="ghost"
                  onClick={() => update.mutate('IGNORED')}
                  loading={update.isPending && update.variables === 'IGNORED'}
                >
                  Ignorar
                </Button>
                {e.status === 'NEW' && (
                  <Button
                    icon={<Search />}
                    onClick={() => update.mutate('INVESTIGATING')}
                    loading={update.isPending && update.variables === 'INVESTIGATING'}
                  >
                    En revisión
                  </Button>
                )}
                <Button
                  variant="primary"
                  icon={<CircleCheck />}
                  onClick={() => update.mutate('RESOLVED')}
                  loading={update.isPending && update.variables === 'RESOLVED'}
                >
                  Marcar como resuelto
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </Dialog>
  );
}
